// PracticeView.jsx — Practice Studio (/practice)
// Flash-design training ground for apprentice artists. Shift-less: no queue,
// no printer, no orders — the only data it touches is `practiceReps` (its own
// collection), `config/practiceLadder`, `config/flashDesigns`, and read-only
// "study material" queries against finished orders of the same design.
//
// LOAD-BEARING: all progress (XP, rank, ladder completion) is DERIVED from
// the rep log via src/utils/practice.js — never stored. See that file's
// header before adding any gamification.
//
// Rendered for both roles: the admin (from the staff menu) and "artist"-role
// accounts (whose ONLY route this is — App.js redirects them here from
// everything else, and Firestore rules enforce the same boundary).

import { useEffect, useMemo, useRef, useState } from "react";
import { auth, db, storage } from "../firebase";
import {
  collection, query, where, onSnapshot, addDoc, getDocs, doc, setDoc, updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import useFlashDesigns from "../hooks/useFlashDesigns";
import useArtists, { artistNameKeys } from "../hooks/useArtists";
import GriddedReference from "../components/GriddedReference";
import GridOverlay from "../GridOverlay";
import ArtistAvatar, { avatarFrameForXp, AVATAR_BASES, AVATAR_BGS } from "../components/ArtistAvatar";
import {
  derivePracticeProgress, repTotalXp, repBaseXp, formatDrawTime, PRACTICE_XP,
  deriveLiveStats, rankForXp, PRACTICE_RANKS, earnedGear, deriveBadges, avatarItems,
} from "../utils/practice";
import { STAFF_THEME, FONT } from "../theme/staff";

const BRAND = STAFF_THEME;

// Resize + JPEG-compress a camera capture before upload (same pattern as
// HistoricalUploadModal / FlashBoardSubmit — practice photos don't need
// full-res originals).
async function compressImage(file, maxDim = 1600, quality = 0.85) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return await new Promise((res) =>
      canvas.toBlob((b) => res(b || file), "image/jpeg", quality)
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function PracticeView({ user, navigate, role = "admin", artistName, artistId }) {
  const { allDesigns, designMap } = useFlashDesigns(null);
  const { allArtists, artistMap, byName, loading: artistsLoading } = useArtists();

  // Resolve my registry identity: explicit artistId (artist logins carry it
  // from staffRoles), else email match, else name match. May be null — reps
  // still log under the display name and tether when the registry entry lands.
  const fallbackName = artistName || user?.displayName || "Artist";
  const registryArtist =
    (artistId && artistMap[artistId]) ||
    Object.values(artistMap).find((a) => a.email && a.email === user?.email) ||
    byName[fallbackName.trim().toLowerCase()] ||
    null;
  const name = registryArtist?.name || fallbackName;

  const [reps, setReps] = useState([]);
  const [ladderSteps, setLadderSteps] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [openDesign, setOpenDesign] = useState(undefined); // undefined=home, null=freeform, string=designId
  const [xpToast, setXpToast] = useState(null);

  // My rep log — client-sorted (where + orderBy would need a composite index).
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "practiceReps"), where("artistUid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setReps(rows);
    });
  }, [user?.uid]);

  // Bootstrap: the admin account is an artist too. If nothing in the registry
  // is tethered to this login (by email or the historical name "Dan"), seed
  // the entry once — this is what lets the admin's whole shift history count.
  const seededRef = useRef(false);
  useEffect(() => {
    if (role !== "admin" || artistsLoading || seededRef.current) return;
    const tethered = allArtists.some(
      (a) => (a.email && a.email === user?.email) || artistNameKeys(a).includes("dan")
    );
    if (tethered) return;
    seededRef.current = true;
    setDoc(doc(db, "artists", "dan"), {
      name: "Dan", email: user?.email || null, aliases: [], active: true, addedAt: Date.now(),
    }, { merge: true }).catch(() => {});
  }, [role, artistsLoading, allArtists, user?.email]);

  // Shift summaries — one-shot; live XP derives from artistBreakdown (updates
  // at shift close, which keeps this screen off the live-booth listen paths).
  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, "shift_summaries"))
      .then((snap) => { if (!cancelled) setSummaries(snap.docs.map((d) => d.data())); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Curated ladder — config/practiceLadder { steps: [...] }
  useEffect(() => {
    return onSnapshot(doc(db, "config", "practiceLadder"), (snap) => {
      setLadderSteps(snap.exists() && Array.isArray(snap.data().steps) ? snap.data().steps : []);
    });
  }, []);

  const progress = useMemo(
    () => derivePracticeProgress(reps, ladderSteps),
    [reps, ladderSteps]
  );

  // Live-shift XP — retroactive, matched by registry name(s) against every
  // shift summary's per-artist breakdown. Combined XP drives the rank.
  const live = useMemo(
    () => deriveLiveStats(registryArtist ? artistNameKeys(registryArtist) : [name], summaries),
    [registryArtist, name, summaries]
  );
  const combinedXp = progress.totalXp + live.xp;
  const combinedRank = rankForXp(combinedXp);

  function celebrate(xp) {
    setXpToast(`+${xp} XP`);
    setTimeout(() => setXpToast(null), 2600);
  }

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, fontFamily: FONT.body, color: BRAND.text, paddingBottom: "3rem" }}>
      {/* Header */}
      <div style={{
        padding: "0.75rem 1rem", background: BRAND.panel,
        borderBottom: `1px solid ${BRAND.border}`,
        display: "flex", alignItems: "center", gap: "0.75rem",
      }}>
        <div style={{ color: BRAND.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>
          🎓 Practice Studio
        </div>
        {role === "artist" ? (
          <button onClick={() => auth.signOut()} style={chipBtn()}>Sign out</button>
        ) : (
          <>
            <button onClick={() => navigate("/library?tab=practice")} style={chipBtn()}>📥 Reviews</button>
            <button onClick={() => navigate("/menu")} style={chipBtn()}>← Menu</button>
          </>
        )}
      </div>

      {xpToast && (
        <div style={{
          position: "fixed", top: "4rem", left: "50%", transform: "translateX(-50%)",
          background: BRAND.gold, color: BRAND.dark, fontFamily: FONT.display,
          fontWeight: 800, fontSize: "1.1rem", padding: "0.5rem 1.2rem",
          borderRadius: "999px", zIndex: 50, boxShadow: "0 6px 24px rgba(252,179,31,0.45)",
        }}>
          {xpToast} ✨
        </div>
      )}

      {openDesign === undefined ? (
        <PracticeHome
          name={name}
          progress={progress}
          live={live}
          combinedXp={combinedXp}
          combinedRank={combinedRank}
          reps={reps}
          allDesigns={allDesigns}
          designMap={designMap}
          registryArtist={registryArtist}
          onOpen={setOpenDesign}
        />
      ) : (
        <DesignPractice
          user={user}
          name={name}
          registryArtistId={registryArtist?.id || null}
          design={openDesign ? designMap[openDesign] : null}
          reps={reps}
          onBack={() => setOpenDesign(undefined)}
          onSaved={celebrate}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Home — rank header, ladder, design grid, freeform
// ─────────────────────────────────────────────────────────────────────────────
function PracticeHome({ name, progress, live, combinedXp, combinedRank, reps, allDesigns, designMap, registryArtist, onOpen }) {
  const { current, next } = combinedRank;
  const span = next ? next.xp - current.xp : 1;
  const into = next ? combinedXp - current.xp : 1;
  const pct = Math.min(100, Math.round((into / span) * 100));
  const [avatarOpen, setAvatarOpen] = useState(false);

  return (
    <div style={{ maxWidth: "640px", margin: "0 auto", padding: "1rem" }}>
      {/* Rank card */}
      <div style={{
        background: BRAND.panel, borderRadius: "16px", padding: "1rem 1.2rem",
        border: `1px solid ${BRAND.border}`, marginBottom: "1rem",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <ArtistAvatar avatar={registryArtist?.avatar} fallbackEmoji={current.icon} size={52} frame={avatarFrameForXp(combinedXp)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FONT.display, fontWeight: 800, fontSize: "1.15rem", color: BRAND.gold }}>
              {current.icon} {current.name}
            </div>
            <div style={{ color: BRAND.muted, fontSize: "0.8rem" }}>
              {name} · {combinedXp} XP
            </div>
            <div style={{ color: BRAND.dim, fontSize: "0.72rem", marginTop: "0.1rem" }}>
              📝 practice {progress.totalXp}
              {live.totalOrders > 0 && <> · 🥞 booth {live.xp} ({live.totalOrders} real pancake{live.totalOrders === 1 ? "" : "s"})</>}
            </div>
          </div>
        </div>
        {next && (
          <div style={{ marginTop: "0.7rem" }}>
            <div style={{ height: "10px", background: "rgba(0,0,0,0.4)", borderRadius: "999px", overflow: "hidden" }}>
              <div style={{
                width: `${pct}%`, height: "100%", borderRadius: "999px",
                background: `linear-gradient(90deg, ${BRAND.orange}, ${BRAND.gold})`,
                transition: "width 0.6s",
              }} />
            </div>
            <div style={{ color: BRAND.dim, fontSize: "0.72rem", marginTop: "0.3rem" }}>
              {next.xp - combinedXp} XP to {next.icon} {next.name} — unlocks <b style={{ color: BRAND.gold }}>{next.gear}</b>
            </div>
          </div>
        )}
        {/* Character gear shelf — one item per rank earned */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginTop: "0.6rem", alignItems: "center" }}>
          {earnedGear(combinedXp).map((g) => (
            <span key={g} style={{
              background: "rgba(252,179,31,0.1)", border: "1px solid rgba(252,179,31,0.35)",
              color: BRAND.cream, borderRadius: "999px", padding: "0.18rem 0.55rem",
              fontSize: "0.68rem", fontWeight: 700,
            }}>
              {g}
            </span>
          ))}
          {registryArtist && (
            <button onClick={() => setAvatarOpen(true)} style={{
              background: "transparent", border: `1px dashed ${BRAND.border}`,
              color: BRAND.muted, borderRadius: "999px", padding: "0.18rem 0.6rem",
              fontSize: "0.68rem", fontWeight: 700, cursor: "pointer", marginLeft: "auto",
            }}>
              🎭 Customize
            </button>
          )}
        </div>
      </div>

      {avatarOpen && registryArtist && (
        <AvatarEditor
          artist={registryArtist}
          items={avatarItems(combinedXp, deriveBadges(reps, live))}
          frame={avatarFrameForXp(combinedXp)}
          onClose={() => setAvatarOpen(false)}
        />
      )}

      {/* Journey trail — the ladder as a path with progress rings */}
      <SectionLabel>Training Journey</SectionLabel>
      {progress.steps.length === 0 ? (
        <div style={{ color: BRAND.dim, fontSize: "0.85rem", padding: "0.5rem 0.2rem 1rem" }}>
          No ladder set yet — pick any design below and start logging reps.
        </div>
      ) : (
        <div style={{ position: "relative", marginBottom: "1.2rem" }}>
          {/* the trail line, behind the nodes */}
          <div style={{
            position: "absolute", left: "27px", top: "16px", bottom: "16px",
            width: "3px", borderRadius: "2px",
            background: "rgba(74,164,225,0.22)",
          }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
            {progress.steps.map(({ step, progress: p }, i) => {
              const d = designMap[step.designId];
              const isCurrent = i === progress.currentStepIndex;
              const goalUnits = p.paperGoal + p.griddleGoal + (p.ratingGoal > 0 ? 1 : 0);
              const doneUnits = p.paperDone + p.griddleDone + (p.ratingGoal > 0 && p.bestRating >= p.ratingGoal ? 1 : 0);
              const frac = p.complete ? 1 : goalUnits > 0 ? doneUnits / goalUnits : 0;
              return (
                <button
                  key={`${step.designId}_${i}`}
                  onClick={() => onOpen(step.designId)}
                  style={{
                    position: "relative",
                    display: "flex", alignItems: "center", gap: "0.7rem", textAlign: "left",
                    background: isCurrent ? "rgba(252,179,31,0.1)" : "transparent",
                    border: isCurrent ? `1px solid ${BRAND.gold}` : "1px solid transparent",
                    borderRadius: "14px", padding: "0.3rem 0.5rem 0.3rem 0.1rem", cursor: "pointer",
                    color: BRAND.text, fontFamily: FONT.body, width: "100%",
                  }}
                >
                  <ProgressRing
                    frac={frac}
                    color={p.complete ? BRAND.gold : isCurrent ? BRAND.orange : BRAND.sky}
                  >
                    {d?.imageUrl
                      ? <img src={d.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", opacity: p.complete ? 0.85 : 1 }} />
                      : <span style={{ fontSize: "1rem" }}>🥞</span>}
                    {p.complete && (
                      <div style={{
                        position: "absolute", inset: 0, display: "flex", alignItems: "center",
                        justifyContent: "center", background: "rgba(0,0,0,0.35)",
                        fontSize: "1.1rem",
                      }}>✅</div>
                    )}
                  </ProgressRing>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                      {isCurrent && !p.complete ? "▶ " : ""}{d?.label || step.designId}
                      {isCurrent && !p.complete && (
                        <span style={{ color: BRAND.gold, fontSize: "0.68rem", fontWeight: 800, marginLeft: "0.4rem", letterSpacing: "0.08em" }}>UP NEXT</span>
                      )}
                    </div>
                    <div style={{ color: BRAND.muted, fontSize: "0.74rem" }}>
                      {p.paperGoal > 0 && `📝 ${p.paperDone}/${p.paperGoal}`}
                      {p.griddleGoal > 0 && `  🥞 ${p.griddleDone}/${p.griddleGoal}`}
                      {p.ratingGoal > 0 && `  ⭐ ${p.bestRating >= p.ratingGoal ? p.ratingGoal + "★ earned" : `need a ${p.ratingGoal}★ from Dan`}`}
                    </div>
                  </div>
                  <div style={{ color: p.complete ? BRAND.gold : BRAND.dim, fontFamily: FONT.display, fontWeight: 800, fontSize: "0.78rem", flexShrink: 0 }}>
                    +{step.xp || PRACTICE_XP.ladderStep}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Freeform */}
      <button
        onClick={() => onOpen(null)}
        style={{
          width: "100%", background: `linear-gradient(135deg, ${BRAND.pink}, ${BRAND.orange})`,
          color: "white", border: "none", borderRadius: "12px", padding: "0.8rem",
          fontFamily: FONT.display, fontWeight: 800, fontSize: "0.95rem",
          cursor: "pointer", marginBottom: "0.5rem",
        }}
      >
        🎨 Freeform — just play
      </button>

      {/* Print shop — blank scratch sheets + batch design packs, actual size */}
      <PrintShop designs={allDesigns} />

      {/* Badge shelf — earned bright, unearned dim with the how-to */}
      <SectionLabel>Badges</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "0.5rem", marginBottom: "1.2rem" }}>
        {deriveBadges(reps, live).map((b) => (
          <div key={b.id} title={b.desc} style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            background: b.earned ? "rgba(252,179,31,0.1)" : BRAND.panel,
            border: b.earned ? "1px solid rgba(252,179,31,0.45)" : `1px solid ${BRAND.border}`,
            borderRadius: "12px", padding: "0.45rem 0.6rem",
            opacity: b.earned ? 1 : 0.55,
          }}>
            <span style={{ fontSize: "1.25rem", filter: b.earned ? "none" : "grayscale(1)" }}>{b.icon}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "0.74rem", fontWeight: 800, fontFamily: FONT.display, color: b.earned ? BRAND.gold : BRAND.muted }}>
                {b.name}
              </div>
              <div style={{ fontSize: "0.62rem", color: BRAND.dim, lineHeight: 1.25 }}>{b.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Ranks & gear board — the long game, RPG-style */}
      <RanksBoard combinedXp={combinedXp} />

      {/* All designs */}
      <SectionLabel>Flash Catalog</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))", gap: "0.6rem" }}>
        {allDesigns.map((d) => {
          const mine = reps.filter((r) => r.designId === d.id);
          const best = mine.reduce((b, r) => Math.max(b, r.review?.rating || 0), 0);
          return (
            <button
              key={d.id}
              onClick={() => onOpen(d.id)}
              style={{
                background: BRAND.panel, border: `1px solid ${BRAND.border}`,
                borderRadius: "12px", padding: "0.5rem", cursor: "pointer",
                color: BRAND.text, fontFamily: FONT.body,
              }}
            >
              <div style={{ aspectRatio: "1/1", borderRadius: "8px", overflow: "hidden", background: "rgba(0,0,0,0.35)", marginBottom: "0.35rem" }}>
                {d.imageUrl && <img src={d.imageUrl} alt={d.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
              </div>
              <div style={{ fontSize: "0.76rem", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {d.label}
              </div>
              <div style={{ fontSize: "0.66rem", color: BRAND.muted }}>
                {mine.length > 0 ? `${mine.length} rep${mine.length === 1 ? "" : "s"}` : "not yet"}
                {best > 0 && ` · ${"⭐".repeat(best)}`}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Freeform prompts — play seeds. Warm/silly/earnest, never a test.
// ─────────────────────────────────────────────────────────────────────────────
const FREEFORM_PROMPTS = [
  "A snail wearing a top hat", "A very proud potato", "Grumpy little rain cloud",
  "Cat loafing like a loaf of bread", "Roller-skating ghost", "Sunflower in sunglasses",
  "A dragon who loves tea", "Sleepy crescent moon", "Octopus juggling donuts",
  "A brave little mushroom", "Dinosaur birthday party", "Frog on a lily pad stage",
  "A whale holding an umbrella", "Robot flipping pancakes", "Bumblebee ballerina",
  "A castle riding a turtle", "Campfire made of ice cream cones", "Fox in a cozy scarf",
  "The world's happiest pickle", "A cloud walking a kite on a leash",
  "Penguin barista", "A banana rock star", "Two mice sharing spaghetti",
  "A lighthouse with a face",
];

// ─────────────────────────────────────────────────────────────────────────────
// Design practice screen — reference, study strip, rep loop, history, print
// ─────────────────────────────────────────────────────────────────────────────
function DesignPractice({ user, name, registryArtistId, design, reps, onBack, onSaved }) {
  const [medium, setMedium] = useState("paper");
  const [phase, setPhase] = useState("idle"); // idle | timing | uploading
  const [elapsed, setElapsed] = useState(0);
  const [studyOrders, setStudyOrders] = useState([]);
  // Freeform play tools: an uploaded reference image + a rolled prompt
  const [freeRef, setFreeRef] = useState(null); // storage URL
  const [refBusy, setRefBusy] = useState(false);
  const [prompt, setPrompt] = useState(null);
  // Print options
  const [printOpen, setPrintOpen] = useState(false);
  const [printInk, setPrintInk] = useState("light"); // color | light | gray
  const [printMirrored, setPrintMirrored] = useState(true);
  const [printGrid, setPrintGrid] = useState(true);
  const startRef = useRef(null);
  const fileRef = useRef(null);
  const refFileRef = useRef(null);
  const drawTimeRef = useRef(0);

  const referenceUrl = design?.imageUrl || freeRef || null;

  const myReps = reps.filter((r) => (design ? r.designId === design.id : !r.designId));

  // Timer tick
  useEffect(() => {
    if (phase !== "timing") return;
    const t = setInterval(() => setElapsed(Date.now() - startRef.current), 500);
    return () => clearInterval(t);
  }, [phase]);

  // Study material — real finished pancakes of this design (one-shot).
  // Equality-only filters → no composite index needed.
  useEffect(() => {
    if (!design?.id) { setStudyOrders([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "orders"),
          where("designId", "==", design.id),
          where("state", "==", "done"),
        ));
        if (cancelled) return;
        const rows = snap.docs
          .map((d) => d.data())
          .filter((o) => (o.photo_cutout_url || o.photo_url) && !o.hidden && !o.trashedAt && !o._isTestData)
          .sort((a, b) => (b.photographedAt?.seconds || 0) - (a.photographedAt?.seconds || 0))
          .slice(0, 8);
        setStudyOrders(rows);
      } catch {
        setStudyOrders([]);
      }
    })();
    return () => { cancelled = true; };
  }, [design?.id]);

  function startRep() {
    startRef.current = Date.now();
    setElapsed(0);
    setPhase("timing");
  }

  function finishRep() {
    drawTimeRef.current = Date.now() - startRef.current;
    // Open the camera; saving happens in handleFile (or via "log without photo")
    fileRef.current?.click();
  }

  async function saveRep(photoUrl) {
    setPhase("uploading");
    try {
      const repDoc = {
        artistUid: user.uid, // auth identity — rules key own-rep writes on this
        artistId: registryArtistId, // registry identity — analytics group on this
        artistName: name,
        artistEmail: user.email || null,
        designId: design?.id || null,
        designLabel: design?.label || "Freeform",
        // Freeform context — what they were riffing on, for the review feed
        referenceUrl: !design ? freeRef || null : null,
        promptText: !design ? prompt || null : null,
        medium,
        drawTimeMs: drawTimeRef.current,
        photoUrl: photoUrl || null,
        reviewed: false,
        review: null,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, "practiceReps"), repDoc);
      onSaved(repBaseXp(repDoc));
      setPhase("idle");
    } catch (err) {
      alert(`Couldn't save that rep: ${err?.message || err}`);
      setPhase("idle");
    }
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return; // camera cancelled — rep still timed, offer buttons again
    setPhase("uploading");
    try {
      const blob = await compressImage(file);
      const path = `practice/${user.uid}/${Date.now()}.jpg`;
      const r = storageRef(storage, path);
      await uploadBytes(r, blob, { contentType: "image/jpeg" });
      const url = await getDownloadURL(r);
      await saveRep(url);
    } catch (err) {
      alert(`Photo upload failed: ${err?.message || err}`);
      setPhase("timing"); // photo failed but the rep is still timed — retry or log without
    }
  }

  // Freeform reference upload — compressed + stored so it can ride the rep
  // into Dan's review feed (and print).
  async function handleRefFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setRefBusy(true);
    try {
      const blob = await compressImage(file);
      const r = storageRef(storage, `practice/${user.uid}/ref_${Date.now()}.jpg`);
      await uploadBytes(r, blob, { contentType: "image/jpeg" });
      setFreeRef(await getDownloadURL(r));
    } catch (err) {
      alert(`Couldn't load that image: ${err?.message || err}`);
    }
    setRefBusy(false);
  }

  return (
    <div style={{ maxWidth: "640px", margin: "0 auto", padding: "1rem" }}>
      <button onClick={onBack} style={{ ...chipBtn(), marginBottom: "0.8rem" }}>← All designs</button>

      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.6rem" }}>
        <div style={{ fontFamily: FONT.display, fontWeight: 800, fontSize: "1.3rem", color: BRAND.gold, flex: 1 }}>
          {design ? design.label : "🎨 Freeform"}
        </div>
        {referenceUrl && (
          <button onClick={() => setPrintOpen(true)} style={chipBtn()}>🖨 Print</button>
        )}
      </div>

      {!design && prompt && (
        <div style={{
          background: "rgba(239,83,126,0.14)", border: `1px solid ${BRAND.pink}`,
          borderRadius: "12px", padding: "0.6rem 0.9rem", marginBottom: "0.6rem",
          fontFamily: FONT.display, fontWeight: 700, fontSize: "0.95rem", textAlign: "center",
        }}>
          🎲 {prompt}
        </div>
      )}

      <GriddedReference imageUrl={referenceUrl} label={design?.label || "reference"} />

      {!design && (
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "0.6rem", flexWrap: "wrap" }}>
          <button onClick={() => refFileRef.current?.click()} disabled={refBusy} style={chipBtn()}>
            {refBusy ? "Loading…" : freeRef ? "📷 Swap reference" : "📷 Use a reference image"}
          </button>
          <button
            onClick={() => setPrompt(FREEFORM_PROMPTS[Math.floor(Math.random() * FREEFORM_PROMPTS.length)])}
            style={chipBtn()}
          >
            🎲 Roll a prompt
          </button>
          {(freeRef || prompt) && (
            <button onClick={() => { setFreeRef(null); setPrompt(null); }} style={chipBtn()}>✕ Clear</button>
          )}
          <input ref={refFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleRefFile} />
        </div>
      )}

      {/* Rep controls */}
      <div style={{
        background: BRAND.panel, border: `1px solid ${BRAND.border}`,
        borderRadius: "14px", padding: "0.9rem", marginTop: "1rem",
      }}>
        {phase === "idle" && (
          <>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.7rem" }}>
              <MediumChip active={medium === "paper"} onClick={() => setMedium("paper")} label={`📝 Paper (+${PRACTICE_XP.paperRep} XP)`} />
              <MediumChip active={medium === "griddle"} onClick={() => setMedium("griddle")} label={`🥞 Griddle (+${PRACTICE_XP.griddleRep} XP)`} />
            </div>
            <button onClick={startRep} style={bigBtn(BRAND.gold, BRAND.dark)}>▶ Start rep</button>
          </>
        )}
        {phase === "timing" && (
          <>
            <div style={{ textAlign: "center", fontFamily: FONT.display, fontWeight: 800, fontSize: "2rem", color: BRAND.gold, marginBottom: "0.6rem" }}>
              {formatDrawTime(elapsed)}
            </div>
            <button onClick={finishRep} style={bigBtn(BRAND.green, "white")}>✔ Done — snap it</button>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
              <button
                onClick={() => { drawTimeRef.current = Date.now() - startRef.current; saveRep(null); }}
                style={{ ...chipBtn(), flex: 1 }}
              >
                Log without photo
              </button>
              <button onClick={() => setPhase("idle")} style={{ ...chipBtn(), flex: 1 }}>✕ Cancel</button>
            </div>
          </>
        )}
        {phase === "uploading" && (
          <div style={{ textAlign: "center", color: BRAND.muted, padding: "1rem", fontFamily: FONT.display }}>
            Saving your rep…
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleFile} />
      </div>

      {/* Study material */}
      {studyOrders.length > 0 && (
        <>
          <SectionLabel style={{ marginTop: "1.2rem" }}>The real thing — off the griddle</SectionLabel>
          <div style={{ display: "flex", gap: "0.5rem", overflowX: "auto", paddingBottom: "0.4rem" }}>
            {studyOrders.map((o, i) => (
              <img
                key={i}
                src={o.cutoutThumbnailUrl || o.photo_cutout_url || o.thumbnailUrl || o.photo_url}
                alt={o.designLabel || "pancake"}
                style={{
                  width: "108px", height: "108px", objectFit: "contain",
                  background: "rgba(0,0,0,0.35)", borderRadius: "10px", flexShrink: 0,
                  filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.4))",
                }}
              />
            ))}
          </div>
        </>
      )}

      {/* My history for this design */}
      {myReps.length > 0 && (
        <>
          <SectionLabel style={{ marginTop: "1.2rem" }}>
            Your reps ({myReps.length})
          </SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {myReps.map((r) => (
              <div key={r.id} style={{
                display: "flex", gap: "0.7rem", alignItems: "center",
                background: BRAND.panel, border: `1px solid ${BRAND.border}`,
                borderRadius: "12px", padding: "0.5rem 0.7rem",
              }}>
                <div style={{ width: "56px", height: "56px", borderRadius: "8px", overflow: "hidden", background: "rgba(0,0,0,0.35)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {r.photoUrl
                    ? <img src={r.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <span style={{ fontSize: "1.2rem" }}>{r.medium === "griddle" ? "🥞" : "📝"}</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 700 }}>
                    {r.medium === "griddle" ? "🥞 Griddle" : "📝 Paper"} · {formatDrawTime(r.drawTimeMs)}
                  </div>
                  <div style={{ fontSize: "0.72rem", color: BRAND.muted }}>
                    {r.review?.rating
                      ? <>{"⭐".repeat(r.review.rating)}{r.review.note ? ` — ${r.review.note}` : ""}</>
                      : "awaiting Dan's review"}
                  </div>
                </div>
                <div style={{ color: BRAND.gold, fontFamily: FONT.display, fontWeight: 800, fontSize: "0.78rem", flexShrink: 0 }}>
                  +{repTotalXp(r)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Print options modal */}
      {printOpen && (
        <div
          className="practice-no-print"
          onClick={() => setPrintOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 60,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: BRAND.panel, border: `1px solid ${BRAND.border}`,
            borderRadius: "16px", padding: "1.1rem", width: "100%", maxWidth: "380px",
          }}>
            <div style={{ fontFamily: FONT.display, fontWeight: 800, fontSize: "1.05rem", color: BRAND.gold, marginBottom: "0.7rem" }}>
              🖨 Print {design ? design.label : "reference"}
            </div>
            <div style={{ color: BRAND.muted, fontSize: "0.76rem", marginBottom: "0.7rem", lineHeight: 1.45 }}>
              Prints at <b>actual griddle size</b> — a 6″ circle. In the print
              dialog keep scale at <b>100%</b> (no "fit to page").
            </div>
            <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.6rem" }}>
              <MediumChip active={printInk === "color"} onClick={() => setPrintInk("color")} label="🎨 Full color" />
              <MediumChip active={printInk === "light"} onClick={() => setPrintInk("light")} label="💧 Light ink" />
              <MediumChip active={printInk === "gray"} onClick={() => setPrintInk("gray")} label="◻ Grayscale" />
            </div>
            <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.9rem" }}>
              <MediumChip active={printMirrored} onClick={() => setPrintMirrored((m) => !m)} label="🪞 Mirrored" />
              <MediumChip active={printGrid} onClick={() => setPrintGrid((g) => !g)} label="⊞ Grid" />
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button onClick={() => setPrintOpen(false)} style={{ ...chipBtn(), flex: 1 }}>Cancel</button>
              <button
                onClick={() => setTimeout(() => window.print(), 60)}
                style={{ ...bigBtn(BRAND.gold, BRAND.dark), flex: 2, padding: "0.6rem" }}
              >
                🖨 Print
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print sheet — hidden on screen; the ONLY visible thing when printing.
          576 CSS px = exactly 6in (96px/in), the real griddle circle, as long
          as the print dialog stays at 100% scale. */}
      {referenceUrl && (
        <div id="practice-print-sheet" style={{ display: "none" }}>
          <style>{`
            @media print {
              body * { visibility: hidden !important; }
              .practice-no-print { display: none !important; }
              #practice-print-sheet, #practice-print-sheet * { visibility: visible !important; }
              #practice-print-sheet {
                display: block !important;
                position: absolute; left: 0; top: 0; width: 100%;
                background: #ffffff; color: #333;
              }
            }
            @page { margin: 0.5in; }
          `}</style>
          <div style={{ fontFamily: "Fredoka, sans-serif", textAlign: "center", paddingTop: "12px" }}>
            <div style={{ fontSize: "20px", fontWeight: 700 }}>
              {design ? design.label : prompt ? `🎲 ${prompt}` : "Freeform reference"}
              {printMirrored ? "  (mirrored)" : ""}
            </div>
            <div style={{ position: "relative", width: "576px", height: "576px", margin: "20px auto 0" }}>
              <div style={{ position: "absolute", inset: 0, transform: printMirrored ? "scaleX(-1)" : "none" }}>
                <img
                  src={referenceUrl}
                  alt=""
                  style={{
                    width: "100%", height: "100%", objectFit: "contain",
                    filter: printInk === "gray"
                      ? "grayscale(1) opacity(0.55)"
                      : printInk === "light"
                        ? "opacity(0.5)"
                        : "none",
                  }}
                />
              </div>
              {printGrid && <GridOverlay size={576} color="#999999" opacity={0.5} showLabels side="left" />}
            </div>
            <div style={{ fontSize: "12px", color: "#888", marginTop: "14px" }}>
              The Joy of Pancakes · actual griddle size (6″ circle, ½″ cells) · print at 100% scale
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────────────────────
function ProgressRing({ frac, color, size = 54, children }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ position: "absolute", inset: 0, transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.12)" strokeWidth="4" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="4" fill="none"
          strokeDasharray={`${c * Math.max(0, Math.min(1, frac))} ${c}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.5s" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: "6px", borderRadius: "50%", overflow: "hidden",
        background: "#0d1833", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {children}
      </div>
    </div>
  );
}

// Print Shop — batch sheet printing at actual griddle size: blank grids
// (scratch paper) and design packs (per-design copy counts), one 6″ circle
// per page. Shares the #practice-print-sheet visibility CSS; only mounted on
// the home screen, so it never collides with a design screen's sheet.
function PrintShop({ designs }) {
  const [open, setOpen] = useState(false);
  const [blankCount, setBlankCount] = useState(0);
  const [counts, setCounts] = useState({}); // designId → copies
  const [ink, setInk] = useState("light");
  const [mirrored, setMirrored] = useState(true);
  const [labels, setLabels] = useState(true);

  const totalPages = blankCount + Object.values(counts).reduce((s, n) => s + n, 0);
  const inkFilter = ink === "gray" ? "grayscale(1) opacity(0.55)" : ink === "light" ? "opacity(0.5)" : "none";

  function bump(id, delta) {
    setCounts((c) => {
      const n = Math.max(0, Math.min(20, (c[id] || 0) + delta));
      const next = { ...c };
      if (n === 0) delete next[id];
      else next[id] = n;
      return next;
    });
  }

  const sheetPage = (key, title, imageUrl) => (
    <div key={key} style={{
      breakAfter: "page", pageBreakAfter: "always",
      fontFamily: "Fredoka, sans-serif", textAlign: "center", paddingTop: "12px",
    }}>
      <div style={{ fontSize: "20px", fontWeight: 700 }}>{title}{imageUrl && mirrored ? "  (mirrored)" : ""}</div>
      <div style={{ position: "relative", width: "576px", height: "576px", margin: "20px auto 0" }}>
        {imageUrl && (
          <div style={{ position: "absolute", inset: 0, transform: mirrored ? "scaleX(-1)" : "none" }}>
            <img src={imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", filter: inkFilter }} />
          </div>
        )}
        <GridOverlay size={576} color="#999999" opacity={0.5} showLabels={labels} side={labels ? "left" : null} />
      </div>
      <div style={{ fontSize: "12px", color: "#888", marginTop: "14px" }}>
        The Joy of Pancakes · actual griddle size (6″ circle, ½″ cells) · print at 100% scale
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="practice-no-print"
        style={{
          width: "100%", background: "transparent", border: `1px dashed ${BRAND.border}`,
          color: BRAND.muted, borderRadius: "12px", padding: "0.55rem",
          fontFamily: FONT.body, fontWeight: 700, fontSize: "0.82rem",
          cursor: "pointer", marginBottom: "1.2rem",
        }}
      >
        🖨 Print Shop — blank grids &amp; design packs
      </button>

      {open && (
        <div
          className="practice-no-print"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 60,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: BRAND.panel, border: `1px solid ${BRAND.border}`,
            borderRadius: "16px", padding: "1.1rem", width: "100%", maxWidth: "420px",
            maxHeight: "85vh", display: "flex", flexDirection: "column",
          }}>
            <div style={{ fontFamily: FONT.display, fontWeight: 800, fontSize: "1.05rem", color: BRAND.gold, marginBottom: "0.5rem" }}>
              🖨 Print Shop
            </div>
            <div style={{ color: BRAND.muted, fontSize: "0.74rem", marginBottom: "0.6rem", lineHeight: 1.45 }}>
              Every page is an <b>actual-size</b> 6″ circle (½″ cells). Keep the
              print dialog at <b>100%</b> scale — no "fit to page".
            </div>

            {/* Shared options */}
            <div style={{ display: "flex", gap: "0.35rem", marginBottom: "0.4rem" }}>
              <MediumChip active={ink === "color"} onClick={() => setInk("color")} label="🎨 Color" />
              <MediumChip active={ink === "light"} onClick={() => setInk("light")} label="💧 Light" />
              <MediumChip active={ink === "gray"} onClick={() => setInk("gray")} label="◻ Gray" />
            </div>
            <div style={{ display: "flex", gap: "0.35rem", marginBottom: "0.7rem" }}>
              <MediumChip active={mirrored} onClick={() => setMirrored((m) => !m)} label="🪞 Mirrored" />
              <MediumChip active={labels} onClick={() => setLabels((l) => !l)} label="🔤 Labels" />
            </div>

            {/* Item list — blanks first, then every design */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", border: `1px solid ${BRAND.border}`, borderRadius: "10px", padding: "0.3rem 0.5rem" }}>
              <PrintRow
                label="◯ Blank grid (scratch paper)"
                count={blankCount}
                onBump={(d) => setBlankCount((n) => Math.max(0, Math.min(20, n + d)))}
              />
              {designs.map((d) => (
                <PrintRow
                  key={d.id}
                  label={d.label}
                  thumb={d.imageUrl}
                  count={counts[d.id] || 0}
                  onBump={(delta) => bump(d.id, delta)}
                />
              ))}
            </div>

            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.8rem" }}>
              <button onClick={() => setOpen(false)} style={{ ...chipBtn(), flex: 1 }}>Close</button>
              <button
                onClick={() => setTimeout(() => window.print(), 60)}
                disabled={totalPages === 0}
                style={{
                  ...bigBtn(totalPages ? BRAND.gold : BRAND.raised, totalPages ? BRAND.dark : BRAND.dim),
                  flex: 2, padding: "0.6rem", cursor: totalPages ? "pointer" : "default",
                }}
              >
                🖨 Print {totalPages > 0 ? `${totalPages} page${totalPages === 1 ? "" : "s"}` : "…"}
              </button>
            </div>
          </div>
        </div>
      )}

      {open && totalPages > 0 && (
        <div id="practice-print-sheet" style={{ display: "none" }}>
          <style>{`
            @media print {
              body * { visibility: hidden !important; }
              .practice-no-print { display: none !important; }
              #practice-print-sheet, #practice-print-sheet * { visibility: visible !important; }
              #practice-print-sheet {
                display: block !important;
                position: absolute; left: 0; top: 0; width: 100%;
                background: #ffffff; color: #333;
              }
            }
            @page { margin: 0.5in; }
          `}</style>
          {designs
            .filter((d) => counts[d.id] > 0)
            .flatMap((d) =>
              Array.from({ length: counts[d.id] }, (_, i) => sheetPage(`${d.id}_${i}`, d.label, d.imageUrl))
            )}
          {Array.from({ length: blankCount }, (_, i) => sheetPage(`blank_${i}`, "Griddle practice sheet", null))}
        </div>
      )}
    </>
  );
}

function PrintRow({ label, thumb, count, onBump }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.3rem 0", borderBottom: "1px solid rgba(74,164,225,0.12)" }}>
      <div style={{ width: "34px", height: "34px", borderRadius: "8px", overflow: "hidden", background: "rgba(0,0,0,0.35)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {thumb ? <img src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <span style={{ color: BRAND.dim }}>◯</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0, fontSize: "0.8rem", fontWeight: 700, color: count > 0 ? BRAND.text : BRAND.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </div>
      <button onClick={() => onBump(-1)} style={{ ...printStepBtn(), opacity: count > 0 ? 1 : 0.35 }}>−</button>
      <div style={{ width: "26px", textAlign: "center", fontFamily: FONT.display, fontWeight: 800, fontSize: "0.85rem", color: count > 0 ? BRAND.gold : BRAND.dim }}>
        {count}
      </div>
      <button onClick={() => onBump(1)} style={printStepBtn()}>+</button>
    </div>
  );
}

function printStepBtn() {
  return {
    background: BRAND.raised, border: `1px solid ${BRAND.border}`, color: BRAND.text,
    borderRadius: "8px", width: "28px", height: "28px", fontSize: "1rem", fontWeight: 800,
    cursor: "pointer", lineHeight: 1, flexShrink: 0,
  };
}

// Avatar editor — base + background + three equip zones (🎩 headgear,
// ✋ held item, 📿 accessory), mix-and-match from EARNED unlocks only
// (rank gear + badge prizes; locked items shown as the carrot). Writes ONLY
// artists/{id}.avatar — the rules carve-out lets an artist update that
// field on their own doc.
function AvatarEditor({ artist, items, frame, onClose }) {
  const a = artist.avatar || {};
  const [base, setBase] = useState(a.base || "🥞");
  const [bg, setBg] = useState(a.bg || AVATAR_BGS[4]);
  const [hat, setHat] = useState(a.hat || null);
  const [held, setHeld] = useState(a.held || a.gear || null); // legacy gear = held
  const [accessory, setAccessory] = useState(a.accessory || null);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await updateDoc(doc(db, "artists", artist.id), { avatar: { base, bg, hat, held, accessory } });
      onClose();
    } catch (err) {
      alert(`Couldn't save your look: ${err?.message || err}`);
      setSaving(false);
    }
  }

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 70,
      display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "18px",
        width: "100%", maxWidth: "420px", maxHeight: "88vh", overflowY: "auto", padding: "1.1rem",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem", paddingTop: "1.2rem" }}>
          <ArtistAvatar avatar={{ base, bg, hat, held, accessory }} size={84} frame={frame} />
          <div>
            <div style={{ fontFamily: FONT.display, fontWeight: 800, fontSize: "1.05rem", color: BRAND.gold }}>🎭 Your character</div>
            <div style={{ color: BRAND.muted, fontSize: "0.72rem" }}>Shows up everywhere you do.</div>
          </div>
        </div>

        <SectionLabel>Base</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: "0.3rem", marginBottom: "0.8rem" }}>
          {AVATAR_BASES.map((b) => (
            <button key={b} onClick={() => setBase(b)} style={{
              fontSize: "1.25rem", padding: "0.3rem 0", borderRadius: "10px", cursor: "pointer",
              background: b === base ? "rgba(252,179,31,0.18)" : "transparent",
              border: b === base ? `1px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
            }}>
              {b}
            </button>
          ))}
        </div>

        <SectionLabel>Background</SectionLabel>
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.8rem" }}>
          {AVATAR_BGS.map((c) => (
            <button key={c} onClick={() => setBg(c)} aria-label={c} style={{
              width: "34px", height: "34px", borderRadius: "50%", background: c, cursor: "pointer",
              border: c === bg ? "3px solid white" : "2px solid rgba(255,255,255,0.2)",
            }} />
          ))}
        </div>

        <EquipZone label="🎩 Headgear" slot="hat" items={items} value={hat} onChange={setHat} />
        <EquipZone label="✋ Held item" slot="held" items={items} value={held} onChange={setHeld} />
        <EquipZone label="📿 Accessory" slot="accessory" items={items} value={accessory} onChange={setAccessory} />

        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button onClick={onClose} style={{ ...chipBtn(), flex: 1 }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...bigBtn(BRAND.gold, BRAND.dark), flex: 2, padding: "0.6rem" }}>
            {saving ? "…" : "Save my look"}
          </button>
        </div>
      </div>
    </div>
  );
}

// One equip zone — chip row of every catalog item for the slot; earned
// items equip on tap, locked ones show their unlock as the carrot.
function EquipZone({ label, slot, items, value, onChange }) {
  const zoneItems = items.filter((it) => it.slot === slot);
  return (
    <div style={{ marginBottom: "0.8rem" }}>
      <SectionLabel>{label}</SectionLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
        <button onClick={() => onChange(null)} style={{
          padding: "0.3rem 0.6rem", borderRadius: "999px", cursor: "pointer",
          background: value === null ? "rgba(252,179,31,0.16)" : "transparent",
          border: value === null ? `1px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
          color: BRAND.muted, fontSize: "0.72rem", fontWeight: 700, fontFamily: FONT.body,
        }}>
          none
        </button>
        {zoneItems.map((it) => {
          const selected = value === it.name;
          return (
            <button
              key={it.name}
              onClick={() => it.earned && onChange(selected ? null : it.name)}
              disabled={!it.earned}
              title={it.earned ? it.name : `Locked — earn: ${it.unlockLabel}`}
              style={{
                padding: "0.3rem 0.6rem", borderRadius: "999px",
                cursor: it.earned ? "pointer" : "default",
                opacity: it.earned ? 1 : 0.4,
                background: selected ? "rgba(252,179,31,0.16)" : "transparent",
                border: selected ? `1px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
                color: BRAND.text, fontSize: "0.72rem", fontWeight: 700, fontFamily: FONT.body,
                display: "flex", alignItems: "center", gap: "0.25rem",
              }}
            >
              {it.name}
              {!it.earned && <span style={{ fontSize: "0.62rem" }}>🔒</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RanksBoard({ combinedXp }) {
  const [open, setOpen] = useState(false);
  const { current } = rankForXp(combinedXp);
  return (
    <div style={{ marginBottom: "1.2rem" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", textAlign: "left", background: BRAND.panel,
          border: `1px solid ${BRAND.border}`, borderRadius: "12px",
          padding: "0.6rem 0.8rem", color: BRAND.text, cursor: "pointer",
          fontFamily: FONT.display, fontWeight: 800, fontSize: "0.85rem",
          display: "flex", alignItems: "center",
        }}
      >
        <span style={{ flex: 1 }}>🏔 The Long Game — ranks &amp; gear</span>
        <span style={{ color: BRAND.dim }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderTop: "none", borderRadius: "0 0 12px 12px", padding: "0.6rem 0.8rem" }}>
          {PRACTICE_RANKS.map((r) => {
            const earned = combinedXp >= r.xp;
            const isCurrent = r.name === current.name;
            return (
              <div key={r.name} style={{
                display: "flex", alignItems: "center", gap: "0.55rem",
                padding: "0.3rem 0.3rem", borderRadius: "8px",
                background: isCurrent ? "rgba(252,179,31,0.12)" : "transparent",
                opacity: earned ? 1 : 0.55,
              }}>
                <span style={{ fontSize: "1.05rem", filter: earned ? "none" : "grayscale(1)" }}>{r.icon}</span>
                <div style={{ flex: 1, minWidth: 0, fontSize: "0.78rem", fontWeight: 700, color: earned ? BRAND.text : BRAND.muted }}>
                  {r.name}{isCurrent && <span style={{ color: BRAND.gold, fontSize: "0.64rem", marginLeft: "0.35rem", letterSpacing: "0.08em" }}>YOU</span>}
                </div>
                <div style={{ fontSize: "0.68rem", color: earned ? BRAND.cream : BRAND.dim, flexShrink: 0 }}>{r.gear}</div>
                <div style={{ fontSize: "0.66rem", color: BRAND.dim, width: "52px", textAlign: "right", flexShrink: 0, fontFamily: FONT.display, fontWeight: 700 }}>
                  {r.xp} XP
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children, style }) {
  return (
    <div style={{
      color: "rgba(255,218,173,0.55)", fontFamily: FONT.display,
      fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.14em",
      textTransform: "uppercase", margin: "0 0 0.5rem", ...style,
    }}>
      {children}
    </div>
  );
}

function MediumChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        background: active ? BRAND.gold : "transparent",
        color: active ? BRAND.dark : BRAND.muted,
        border: active ? `1px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
        borderRadius: "10px", padding: "0.5rem", fontSize: "0.8rem",
        fontWeight: 700, cursor: "pointer", fontFamily: FONT.body,
      }}
    >
      {label}
    </button>
  );
}

function chipBtn() {
  return {
    background: "transparent", border: `1px solid ${BRAND.border}`,
    color: BRAND.muted, borderRadius: "8px", padding: "0.35rem 0.7rem",
    fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT.body,
  };
}

function bigBtn(bg, color) {
  return {
    width: "100%", background: bg, color, border: "none",
    borderRadius: "12px", padding: "0.85rem", fontFamily: FONT.display,
    fontWeight: 800, fontSize: "1.05rem", cursor: "pointer",
  };
}
